import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { XClose } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';

// =============================================================================
// Types
// =============================================================================

export interface StageItem {
  id: string; // crypto.randomUUID() for DnD identity
  name: string;
  order: number; // 1-based
}

// =============================================================================
// Factory function for default stages
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components -- factory function co-located with StageManager component
export function createDefaultStages(): StageItem[] {
  return [
    { id: crypto.randomUUID(), name: 'To Do', order: 1 },
    { id: crypto.randomUUID(), name: 'In Progress', order: 2 },
    { id: crypto.randomUUID(), name: 'Done', order: 3 },
  ];
}

// =============================================================================
// StageRow - Individual editable stage row (for drag overlay preview)
// =============================================================================

interface StageRowProps {
  stage: StageItem;
  onNameChange: (name: string) => void;
  onRemove: () => void;
  disabled: boolean;
  canDelete: boolean;
  nameError?: string;
  dragHandleProps?: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
  } | null;
}

export function StageRow({
  stage,
  onNameChange,
  onRemove,
  disabled,
  canDelete,
  nameError,
  dragHandleProps,
}: StageRowProps) {
  return (
    <div className='space-y-1'>
      <div className='flex items-center gap-2'>
        {/* Drag handle */}
        {dragHandleProps && (
          <button
            type='button'
            {...dragHandleProps.listeners}
            {...dragHandleProps.attributes}
            disabled={disabled}
            className={`flex items-center justify-center w-6 h-6 touch-none rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 ${
              disabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-grab active:cursor-grabbing'
            }`}
            aria-label='Drag to reorder'
          >
            <GripVertical className='size-4 text-text-tertiary' />
          </button>
        )}

        {/* Name input */}
        <div className='flex-1'>
          <InputGroup
            aria-label='Stage name'
            value={stage.name}
            onChange={onNameChange}
            isDisabled={disabled}
            isInvalid={!!nameError}
          >
            <InputBase placeholder='Stage name' />
          </InputGroup>
        </div>

        {/* Delete button — `sm` to match the InputGroup beside it (both 36px
            on the size ladder); `tertiary-destructive` keeps the old
            `hover:text-error` intent on a token instead of a hand-rolled
            hover, and Button supplies the disabled treatment. */}
        <Button
          color='tertiary-destructive'
          size='sm'
          iconLeading={XClose}
          onClick={onRemove}
          isDisabled={disabled || !canDelete}
          aria-label={`Remove ${stage.name || 'stage'}`}
        />
      </div>

      {/* Error display */}
      {nameError && (
        <p className='text-sm text-text-error-primary ml-8' role='alert'>
          {nameError}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// SortableStageRow - Wrapper for drag-and-drop functionality
// =============================================================================

interface SortableStageRowProps extends Omit<StageRowProps, 'dragHandleProps'> {
  id: string;
}

export function SortableStageRow({ id, ...props }: SortableStageRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Drag-over placeholder
  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style}>
        <div className='border-2 border-dashed border-border-secondary rounded-lg opacity-30'>
          <div className='invisible'>
            <StageRow {...props} dragHandleProps={null} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <StageRow {...props} dragHandleProps={{ listeners, attributes }} />
    </div>
  );
}

// =============================================================================
// StageRowPreview - Preview shown during drag
// =============================================================================

interface StageRowPreviewProps {
  stage: StageItem;
}

export function StageRowPreview({ stage }: StageRowPreviewProps) {
  return (
    <div className='bg-bg-primary border border-border-primary rounded-lg shadow-lg px-3 py-2'>
      <div className='flex items-center gap-2'>
        <GripVertical className='size-4 text-text-tertiary' />
        <span className='text-sm text-text-primary flex-1'>{stage.name}</span>
      </div>
    </div>
  );
}
